import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { useBreadcrumb } from '@/shared/context/BreadcrumbContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useState } from 'react'
import {
  ArrowLeft, BarChart3, ClipboardList, Download, Copy, ExternalLink, Info, Loader2,
} from 'lucide-react'
import { toast } from 'sonner'
import { useEncuesta, useToggleMultiplesRespuestas, useExportarRespuestas } from '../hooks/useEncuestas'
import { useCampania } from '../hooks/useCampanias'

export function EncuestasAdminPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const campaniaId = Number(id)

  const [showInfo, setShowInfo] = useState(false)
  const { setOverride, clearOverride } = useBreadcrumb()
  useDocumentTitle('Encuestas')

  const { data: campania } = useCampania(campaniaId)

  useEffect(() => {
    if (campania?.titulo) {
      setOverride(`/muestras-medicas/${id}`, campania.titulo)
    }
    return () => { clearOverride(`/muestras-medicas/${id}`) }
  }, [campania?.titulo, id, setOverride, clearOverride])
  const { data: encuesta, isLoading } = useEncuesta(campaniaId)
  const toggleMultiples = useToggleMultiplesRespuestas(campaniaId)
  const exportar = useExportarRespuestas()

  const publicUrl = encuesta ? `${window.location.origin}/muestras-medicas/encuesta/${encuesta.campania_uuid}` : ''

  const handleCopyUrl = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(publicUrl)
      } else {
        throw new Error('clipboard not available')
      }
    } catch {
      setTimeout(() => {
        const input = document.createElement('input')
        input.value = publicUrl
        input.style.position = 'fixed'
        input.style.left = '-9999px'
        document.body.appendChild(input)
        input.focus()
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
      }, 100)
    }
    toast.success('URL copiada al portapapeles')
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/muestras-medicas/${campaniaId}`)}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver
          </Button>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-teal-500" />
            Encuestas
          </h1>
          {campania && (
            <>
              <button
                onClick={() => setShowInfo(!showInfo)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Info className="h-5 w-5" />
              </button>
              {showInfo && (
                <span className="text-muted-foreground text-sm animate-in slide-in-from-left-2 duration-200">
                  {campania.titulo}
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {encuesta && (
        <div className="grid gap-6 md:grid-cols-2">
          {/* Configuración */}
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="text-lg">Configuración</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Permitir múltiples respuestas</Label>
                <Switch
                  checked={encuesta.permitir_multiples_respuestas}
                  onCheckedChange={() => toggleMultiples.mutate()}
                  className="data-[state=checked]:bg-teal-500"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Total respuestas</Label>
                <span className="text-2xl font-bold">{encuesta.total_respuestas}</span>
              </div>

              <div className="flex items-center justify-between">
                <Label>Estado</Label>
                <span className={`font-medium ${encuesta.activa ? 'text-teal-600' : 'text-muted-foreground'}`}>
                  {encuesta.activa ? 'Activa' : 'Inactiva'}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* URL Pública */}
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="text-lg">URL Pública</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm bg-muted p-2 rounded truncate">{publicUrl}</code>
                <Button variant="outline" size="sm" onClick={handleCopyUrl}>
                  <Copy className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Acciones */}
      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          onClick={() => navigate(`/muestras-medicas/${campaniaId}/encuestas/form-builder`)}
        >
          <ClipboardList className="mr-2 h-4 w-4" /> Form Builder Encuesta
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate(`/muestras-medicas/${campaniaId}/encuestas/analiticas`)}
        >
          <BarChart3 className="mr-2 h-4 w-4" /> Ver Analíticas
        </Button>
        <Button
          variant="outline"
          onClick={() => exportar.mutate(campaniaId)}
          disabled={exportar.isPending}
        >
          {exportar.isPending
            ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            : <Download className="mr-2 h-4 w-4" />
          }
          Exportar Respuestas
        </Button>
      </div>
    </div>
  )
}
